// Reads GET /api/me once per sign in and keeps the chosen workspace. The answer is the source of the role,
// the held approvals and the permission codes. Phases: loading, ready, notConnected (404, 503 or no Engine
// address set at all, so the old Clerk role may stand in), error (anything else, including an Engine address
// that is not usable, which never opens a screen and never falls back to the Clerk role).
// An answer is kept with the sign in it was asked for. Whatever the hook returns is derived from the
// current sign in on every render, so a changed or disabled sign in is least privilege at once, and a
// cancelled or superseded read can never publish.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createAccessClient, isNotConnected } from './engine-access-client.js'
import { deriveAccess } from './access-model.js'

export const CONFIG_ERROR = 'The Engine address is not set up correctly.'

export function useServerAccess({ enabled, getToken, userId, sessionId, baseUrl, allowLocal = false }) {
  const [state, setState] = useState({ key: null, gen: 0, lifecycle: 0, phase: 'loading', me: null, error: null })
  const [wanted, setWanted] = useState(null)
  const [attempt, setAttempt] = useState(0)
  const generation = useRef(0)
  // Bumped on every change of the sign in key, including to disabled and back to the same sign in, so an
  // answer kept from before a disabled interval is never accepted again.
  const epoch = useRef(0)
  const lastKey = useRef(null)

  // getToken may change identity on every render; the client reads the latest one so it is built once per sign in.
  const tokenSource = useRef(getToken)
  tokenSource.current = getToken
  const key = enabled && userId && sessionId ? `${userId}|${sessionId}` : null
  if (lastKey.current !== key) { lastKey.current = key; epoch.current++ }
  const lifecycle = epoch.current
  const setup = useMemo(() => {
    if (!baseUrl) return { client: null, configError: false }
    try { return { client: createAccessClient({ baseUrl, getToken: () => tokenSource.current(), allowLocal }), configError: false } } catch { return { client: null, configError: true } }
  }, [baseUrl, allowLocal, key])
  const { client, configError } = setup

  useEffect(() => {
    if (!key || !client) return undefined
    const controller = new AbortController()
    const gen = ++generation.current
    const publish = next => {
      if (controller.signal.aborted || generation.current !== gen) return
      setState({ key, gen, lifecycle, ...next })
    }
    setState({ key, gen, lifecycle, phase: 'loading', me: null, error: null })
    client.me({ signal: controller.signal }).then(
      me => publish({ phase: 'ready', me, error: null }),
      e => {
        if (e?.name === 'AbortError') return
        publish(isNotConnected(e) ? { phase: 'notConnected', me: null, error: null } : { phase: 'error', me: null, error: e?.message || 'Your access could not be loaded.' })
      },
    )
    return () => { controller.abort(); generation.current++ }
  }, [client, key, attempt, lifecycle])

  // A different person never inherits the previous person's workspace pick.
  useEffect(() => { setWanted(null) }, [userId, sessionId])

  let current
  if (!key) current = { phase: 'loading', me: null, error: null }
  else if (configError) current = { phase: 'error', me: null, error: CONFIG_ERROR }
  else if (!baseUrl) current = { phase: 'notConnected', me: null, error: null }
  else if (state.key === key && state.lifecycle === lifecycle) current = state
  else current = { phase: 'loading', me: null, error: null }

  const access = useMemo(() => (current.phase === 'ready' ? deriveAccess(current.me, wanted) : null), [current.phase, current.me, wanted])
  const retry = useCallback(() => setAttempt(n => n + 1), [])
  return { client: key ? client : null, meState: current.phase, meError: current.error, me: current.me, access, setWorkspaceId: setWanted, retryMe: retry }
}
